import React, { useCallback, useMemo } from "react"

export interface TokensContextValue {
    getAccessToken: () => string | null
    getRefreshToken: () => string | null
    setAccessToken: (accessToken: string) => void
    setRefreshToken: (refreshToken: string) => void
}

export const TokensContext = React.createContext<TokensContextValue>({
    getAccessToken: () => "",
    getRefreshToken: () => "",
    setAccessToken: (accessToken: string) => null,
    setRefreshToken: (refreshToken: string) => null
})

interface TokensProviderProps {
    value?: TokensContextValue
    children: React.ReactNode
}

const ACCESS_TOKEN_KEY = "accessToken"
const REFRESH_TOKEN_KEY = "refreshToken"

export const TokensProvider: React.FunctionComponent<TokensProviderProps> = props => {
    const getAccessToken = useCallback(() => localStorage.getItem(ACCESS_TOKEN_KEY), [])
    const getRefreshToken = useCallback(() => localStorage.getItem(REFRESH_TOKEN_KEY), [])
    const setAccessToken = useCallback(value => localStorage.setItem(ACCESS_TOKEN_KEY, value), [])
    const setRefreshToken = useCallback(value => localStorage.setItem(REFRESH_TOKEN_KEY, value), [])

    const contextValue = useMemo(
        () => ({
            getAccessToken,
            getRefreshToken,
            setAccessToken,
            setRefreshToken
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    )
    return <TokensContext.Provider value={props?.value ?? contextValue} {...props} />
}

export const useTokens = (): TokensContextValue => {
    const context = React.useContext(TokensContext)
    if (!context) throw new Error("useTokens must be used within a TokensProvider")
    return context
}
